import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Transition, TransitionService } from '@uirouter/angular';
import moment from 'moment';
import { merge, ReplaySubject } from 'rxjs';
import { debounceTime, filter, takeWhile, throttleTime } from 'rxjs/operators';
import * as fromLookups from '../../lookups';
import { isElementVisible, IStatusMessage, ResponseStatus } from '../../shared';
import {
    Accordion,
    SelectItem,
    SortEvent,
    Table
} from '../../shared/efaact-primeng.module';
import { IShowErrors } from '../../shared/errors-sidenav';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { CompanySetting } from '../store';

@Component({
    selector: 'ef-company-setting-presenter',
    templateUrl: './company-setting-presenter.component.html',
    styleUrls: ['./company-setting-presenter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CompanySettingPresenterComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private _settings: Array<CompanySetting>;
    moment: any;
    @Input() public set settings(value: Array<CompanySetting>) {
        delete this.editing;
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:settings',
            value,
            tabs: this.openTabs,
            settingsEl: this.settingsEl
        });
        if (value && value.length) {
            this.loading = false;
            this.companySettingsForm = new UntypedFormGroup({});
            this._settings = value.map(x => {
                this.companySettingsForm.addControl(
                    x.SettingType,
                    new UntypedFormControl(x.SettingValue)
                );
                return {
                    ...x,
                    SettingValue:
                        x.CtrlType.toLocaleLowerCase() === 'datetimepicker'
                            ? x.Isreadonly
                                ? x.SettingValue
                                    ? moment(x.SettingValue).format(
                                        'MM/DD/YYYY'
                                    )
                                    : x.SettingValue
                                : x.SettingValue
                                    ? moment(x.SettingValue).toDate()
                                    : x.SettingValue
                            : x.SettingValue
                };
            });
        }
        this.updateRowGroupMetaData(value);
    }
    public get settings(): Array<CompanySetting> {
        return this._settings;
    }
    @Input() public set categories(
        value: { CatID: number; Description: string }[]
    ) {
        if (value && value.length > 0) {
            this.categoryItems = value.map(x => ({
                value: x.CatID,
                label: x.Description
            }));
        }
    }
    categoryItems: SelectItem[];
    @Input() payrollFrequency: Array<{ Payrollfrequency: number }>;
    @Input() daysOfWeek: Array<fromLookups.IDayOfWeek>;
    @Input() CompanySettings: Array<Array<CompanySetting>>;
    private _selectedError: IStatusMessage;
    @Input() public set selectedError(value: IStatusMessage) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:selectedError:set',
            value,
            _selectedError: this._selectedError
        });
        if (!value) {
            return;
        }
        if (this._selectedError && this._selectedError.MsgEntityID) {
            const oldRow = document.getElementById(
                `row-${this._selectedError.MsgEntityID.toString()}`
            );
            if (oldRow) {
                oldRow.classList.remove('highlight');
                oldRow.classList.remove('highlight-warning');
                oldRow.classList.remove('highlight-danger');
            }
        }
        this._selectedError = value;
        if (!(value && value.MsgEntityID)) {
            return;
        }
        const row = document.getElementById(
            `row-${value.MsgEntityID.toString()}`
        );
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:selectedError',
            value,
            row
        });
        if (!row) {
            return;
        }
        if (!isElementVisible(row, row.parentElement)) {
            row.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }

        row.classList.add('highlight');
        if (value.StatusCode === ResponseStatus.Warning) {
            row.classList.add('highlight-warning');
        }
        if (value.StatusCode === ResponseStatus.Failed) {
            row.classList.add('highlight-danger');
        }
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:selectedError',
            classList: row.classList,
            row
        });
        this.cdRef.detectChanges();
    }
    public get selectedError(): IStatusMessage {
        return this._selectedError;
    }
    @Output() public Save = new ReplaySubject<CompanySetting>(1);
    @Output() public ShowError = new EventEmitter<IShowErrors>();

    @ViewChild('settingsAccordion') settingsEl: Accordion;
    @ViewChild('dtSettings') settingsTable: Table;
    companySettingsForm: UntypedFormGroup;
    protected openTabs: Array<number> = [];
    public editing: CompanySetting;
    rowGroupMetadata: any;
    public loading = true;
    private active = true;
    private TextboxChangedSubject = new ReplaySubject<{
        value: string;
        data: CompanySetting;
    }>(1);
    private EditCompleteSubject = new ReplaySubject<{
        value: string;
        data: CompanySetting;
    }>(1);
    private SettingChangedSubject = new ReplaySubject<{
        value: string;
        data: CompanySetting;
    }>(1);
    private tUnregister: Function;
    private me: CompanySettingPresenterComponent;
    textboxValueSaved = false;
    constructor(
        private log: LoggingService,
        private cdRef: ChangeDetectorRef,
        public confirmservice: UnsavedChangesService,
        public transitionService: TransitionService
    ) {
        this.moment = moment;
        this.companySettingsForm = new UntypedFormGroup({});
        this.tUnregister = transitionService.onStart(
            { from: 'companysetting' },
            this.uiCanExit.bind(this)
        );
        this.me = this;
    }
    ngOnInit() {
        this.setupSubscriptions();
    }
    uiCanExit(transition?: Transition) {
        function getControlName(c: AbstractControl): string | null {
            const formGroup = c.parent.controls;
            return (
                Object.keys(formGroup).find(name => c === formGroup[name]) ||
                null
            );
        }
        let result = true;
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:uiCanExit',
            companySettingsForm: this.companySettingsForm,
            transition,
            controls: this.companySettingsForm.controls
        });
        // there should only be one dirty item possible
        // TODO: check for form dirty and save if it is.
        const keys = Object.keys(this.companySettingsForm.controls);
        return new Promise((r, e) => {
            keys.forEach(key => {
                const c = this.companySettingsForm.controls[key];
                const setting = this.settings.find(x => x.SettingType === key);
                if (c.dirty) {
                    this.log.verbose({
                        type: 'CompanySettingPresenterComponent:uiCanExit',
                        value: 'dirty control',
                        key,
                        c
                    });
                    if (
                        setting.Validation &&
                        setting.Validation === 'confirm'
                    ) {
                        this.log.verbose({
                            type: 'CompanySettingPresenterComponent:uiCanExit',
                            value: 'validation control',
                            key,
                            c
                        });
                        result = false;
                        return this.confirmservice
                            .confirmPromise({
                                dialogText: `Are you sure you want to update ${setting.SettingTypeLabel}`
                            })
                            .then(() => {
                                this.settingChanged(c.value, setting);
                                result = true;
                                r(result);
                                // result = result && true;
                            })
                            .catch(() => {
                                result = false;
                                r(result);
                            });
                    } else {
                        this.settingChanged(c.value, setting);
                        // result = result && true;
                    }
                } else {
                    this.log.verbose({
                        type: 'CompanySettingPresenterComponent:uiCanExit',
                        value: 'clean control',
                        key,
                        c
                    });
                }
            });
            if (result) {
                r(true);
            }
        });
    }
    ngOnDestroy() {
        this.active = false;
        this.tUnregister();
    }
    ngAfterViewInit() {}
    setupSubscriptions() {
        this.SettingChangedSubject.pipe(
            takeWhile(() => this.active),
            throttleTime(100)
        ).subscribe(x => {
            this.log.verbose({
                type: 'CompanySettingPresenterComponent:SettingChangedSubject',
                x
            });
            if (
                x.data.SettingType == 'OverrideQBMail' &&
                Boolean(x.value) == false
            ) {
                this.confirmservice
                    .confirmPromise({
                        headerText: 'Are you sure?',
                        dialogText:
                            'turning off this setting will replace all email addresses in eFAACT with the email address entered in their QuickBooks employee/vendor record.',
                        okText: 'OK',
                        cancelText: 'Cancel'
                    })
                    .then(() => {
                        this.settingChanged(x.value, x.data);
                    })
                    .catch(() => {
                        this.settingChanged(!x.value, x.data);
                    });
            } else {
                this.settingChanged(x.value, x.data);
            }
        });

        // const latestChange = this.TextboxChangedSubject.pipe(
        //     map(x => this.EditCompleteSubject),
        //     mergeAll(),
        //     takeWhile(() => this.active)
        // );

        // const latestChange = from([this.TextboxChangedSubject, this.EditCompleteSubject]).pipe(
        //   mergeMap(x => x),
        //   takeWhile(() => this.active)
        // );

        const latestChange = merge(
            // ...[
            this.TextboxChangedSubject.pipe(
                debounceTime(3000),
                filter(x => !x.data.Validation)
            ),
            this.EditCompleteSubject,
            // ]
            2
        ).pipe(
            // mergeAll(),
            takeWhile(() => this.active),
            throttleTime(100)
        );

        // const latestChange = combineLatest([
        //     this.TextboxChangedSubject.pipe(
        //         debounceTime(2000),
        //         startWith(null),
        //         // switchMap(x => this.TextboxChangedSubject)
        //     ),
        //     this.EditCompleteSubject.pipe(
        //         startWith(null),
        //         // switchMap(x => this.EditCompleteSubject)
        //     )
        // ]).pipe(
        //     takeWhile(() => this.active),
        //     filter(([x, y]) => !!x || !!y)
        // );

        latestChange.subscribe(z => {
            // const z = {
            //     value: (x && x.value) || (y && y.value),
            //     data: (x && x.data) || (y && y.data)
            // };
            this.log.verbose({
                type: 'CompanySettingPresenterComponent:latestChange(z)',
                z
            });
            // if (!z.data.Validation) {
            if (this.textboxValueSaved == false) {
                this.SettingChangedSubject.next(z);
            }

            // }
            // this.settingChanged(z.value, z.data);
        });

        // this.TextboxChangedSubject.pipe(
        //     takeWhile(() => this.active),
        //     debounceTime(2000)
        // ).subscribe(event => {
        //     this.settingChanged(event.value, event.data);
        // });
    }
    onSave($event) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onSave',
            $event
        });
        this.Save.next($event);
    }
    onEditInit($event: { data: CompanySetting }) {
        const setting = this.settings.find(
            x => x.SettingType === $event.data.SettingType
        );
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onEditInit',
            $event,
            editing: this.editing,
            setting
        });
        // if (setting.CtrlType !== 'checkbox') {
        // }
        if (
            (!!this.editing &&
                this.editing.SettingType !== $event.data.SettingType) ||
            setting.CtrlType === 'checkbox'
        ) {
            this.onEditComplete({ data: { ...this.editing, ...$event.data } });
        }
        this.editing = { ...$event.data };
    }
    onEditComplete($event: { data: Partial<CompanySetting> }) {
        if (!this.editing) {
            return;
        }
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onEditComplete',
            $event
        });
        const setting = this.settings.find(
            x => x.SettingType === $event.data.SettingType
        );

        let newVal = setting.SettingValue;
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onEditComplete',
            $event,
            setting,
            editing: this.editing
        });
        if (
            !this.hasChangedValue(
                setting && setting.SettingValue,
                this.editing && this.editing.SettingValue,
                setting && setting.CtrlType
            )
        ) {
            delete this.editing;
            return;
        }
        // if (
        //     $event.data.SettingValue !== setting.SettingValue
        //     // || setting.CtrlType === 'checkbox'
        //     // || setting.CtrlType === 'combobox'
        // ) {
        if (setting.CtrlType.toLocaleLowerCase() === 'datetimepicker') {
            newVal = moment(setting.SettingValue).format('MM/DD/YYYY');
            this.SettingChangedSubject.next({ value: newVal, data: setting });
            // this.settingChanged(newVal, setting);
        }
        if (
            setting.CtrlType.toLocaleLowerCase() === 'textbox'
            // && setting.Validation.length
        ) {
            this.SettingChangedSubject.next({ value: newVal, data: setting }); ////////
            this.textboxValueSaved = true;
            // this.settingChanged(newVal, setting);
        }
        //     if (setting.CtrlType === 'checkbox') {
        //         newVal = setting.SettingValue ? '1' : '0';
        //     }
        // }
        // if (!(setting.CtrlType === 'combobox')) {
        //     this.EditCompleteSubject.next({ value: newVal, data: $event.data });
        // }
        // // this.TextboxChangedSubject.next({value: newVal, data: $event.data});
        // this.editing = null;
    }
    onDatepickerClose($event) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onDatepickerClose',
            $event,
            editing: this.editing
        });
    }
    customSort(event: SortEvent) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:customSort',
            event
        });
        event.data.sort((a, b) => {
            const result = a.CatDescription.toLocaleLowerCase().localeCompare(
                b.CatDescription.toLocaleLowerCase()
            );
            return result === 0
                ? a[event.field]
                      .toLocaleLowerCase()
                      .localeCompare(b[event.field].toLocaleLowerCase())
                : result;
        });
    }
    filterTable(value, column, operator) {
        // dtSettings.filter($event.value.Description, 'CatDescription', 'in')
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:filterTable',
            value,
            column,
            operator
        });
        this.settingsTable.filter(value, column, operator);
    }
    onFilter($event) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onFilter',
            $event
        });
        // setTimeout(() => {
        this.onUpdateRowGroupMetaData($event.filteredValue);
        // }, 50);
    }
    onSort($event) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onSort',
            $event
        });
        setTimeout(() => {
            this.onUpdateRowGroupMetaData(
                this.settingsTable.filteredValue || this.settingsTable.value
            );
        }, 50);
    }
    onUpdateRowGroupMetaData(data) {
        this.updateRowGroupMetaData(data);
    }
    updateRowGroupMetaData(data) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:updateRowGroupMetaData',
            data
        });
        this.rowGroupMetadata = {};
        if (data) {
            for (let i = 0; i < data.length; i++) {
                const rowData = data[i];
                const category = rowData.CatID;
                if (i === 0) {
                    this.rowGroupMetadata[category] = { index: 0, size: 1 };
                } else {
                    const previousRowData = data[i - 1];
                    const previousRowGroup = previousRowData.CatID;
                    if (category === previousRowGroup) {
                        this.rowGroupMetadata[category].size++;
                    } else {
                        this.rowGroupMetadata[category] = { index: i, size: 1 };
                    }
                }
            }
            this.log.verbose({
                type: 'CompanySettingPresenterComponent:updateRowGroupMetaData',
                data,
                rowGroupMetadata: this.rowGroupMetadata
            });
            this.cdRef.markForCheck();
        }
    }
    getDescriptionForDropdown(settingType, settingValue) {
        const options = this.getValuesForDropdown(settingType);
        switch (settingType.toLocaleLowerCase()) {
            case 'firstdayofweek':
                return this.daysOfWeek.find(
                    x => x.DayOfWeekID.toString() === settingValue
                ).DayOfWeekDesc;
        }
        return settingValue;
    }
    getValuesForDropdown(settingType) {
        switch (settingType.toLocaleLowerCase()) {
            case 'payrollfrequency':
                return this.payrollFrequency.map(
                    x =>
                        ({
                            label: x.Payrollfrequency.toString(),
                            value: x.Payrollfrequency.toString()
                        } as SelectItem)
                );
                break;
            case 'firstdayofweek':
                return this.daysOfWeek.map(
                    x =>
                        ({
                            label: x.DayOfWeekDesc,
                            value: x.DayOfWeekID.toString()
                        } as SelectItem)
                );
            case '2fa_security_level':
                return [
                    { label: '2', value: '2' },
                    { label: '3', value: '3' }
                ];
            case 'ratetiers':
                return [
                    { label: '2', value: '2' },
                    { label: '3', value: '3' }
                ];
            case 'time_precision':
                return [
                    { label: '0', value: '0' },
                    { label: '1', value: '1' },
                    { label: '2', value: '2' }
                ];
            case 'employeenameorder':
                return [
                    { label: 'First MI Last', value: 'FML' },
                    { label: 'First Last', value: 'FL' },
                    { label: 'Last, First MI', value: 'LFM' },
                    { label: 'Last, First', value: 'LF' }
                ];
        }
        return [];
    }
    settingChanged(value, setting) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:settingChanged',
            value,
            setting,
            editing: this.editing
        });
        if (
            !this.hasChangedValue(
                setting && setting.SettingValue,
                this.editing && this.editing.SettingValue,
                setting && setting.CtrlType
            )
        ) {
            return;
        }

        // const newVal = $event.value || $event;
        // const setting = this.settings.find(
        //     x => x.SettingType === item.SettingType
        // );
        this.updateSetting(value, setting);
        delete this.editing;
        // this.settingsTable.saveRowEdit(null, null);
    }
    updateSetting(value, setting: CompanySetting) {
        const saveSetting = (s, v) => {
            // s.Status = {
            //     StatusCode: ResponseStatus.Loading,
            //     Message: 'Saving'
            // };
            // s.SettingValue = v;

            const ss = {
                ...s,
                Status: {
                    StatusCode: ResponseStatus.Loading,
                    Message: 'Saving'
                },
                SettingValue: v
            };
            this.loading = true;
            this.Save.next(ss);
            // this.editing = null;
        };
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:updateSetting',
            value,
            setting
        });
        if (setting.Validation && setting.Validation.includes('confirm')) {
            this.confirmservice
                .confirmPromise({
                    dialogText: `Are you sure you want to update ${setting.SettingTypeLabel}`
                })
                .then(() => {
                    saveSetting.bind(this.me)(setting, value);
                    return;
                })
                .catch(e => {
                    this.log.verbose({
                        type: 'CompanySettingPresenterComponent:updateSetting:confirm:catch',
                        e,
                        setting,
                        control:
                            this.companySettingsForm.controls[
                                setting.SettingType
                            ].value
                    });
                    setting.SettingValue = this.editing.SettingValue;
                    this.companySettingsForm.controls[
                        setting.SettingType
                    ].reset(setting.SettingValue);
                    this.companySettingsForm.controls[
                        setting.SettingType
                    ].setValue(setting.SettingValue);
                    this.cdRef.detectChanges();
                });
        } else {
            saveSetting.bind(this.me)(setting, value);
        }
    }
    textboxChanged($event, data) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:textboxChanged',
            $event,
            data
        });
        this.textboxValueSaved = false;
        this.TextboxChangedSubject.next({ value: $event, data });
    }
    textboxBlur($event, data: CompanySetting) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:textboxBlur',
            $event,
            data
        });
        if (this.editing && this.editing.SettingValue !== data.SettingValue) {
            this.SettingChangedSubject.next({ value: $event, data: data }); //////
            this.textboxValueSaved = true;
            // this.updateSetting($event, data);
        }
    }
    checkboxChanged($event, data) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:checkboxChanged',
            $event,
            data
        });
        this.SettingChangedSubject.next({ value: $event.checked, data });
    }
    dropdownChanged($event, data) {
        this.SettingChangedSubject.next({ value: $event.value, data });
    }
    onStatusClick(item: CompanySetting) {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:onStatusClick',
            item
        });
        this.ShowError.emit({ open: true, MsgEntityID: item.SettingType });
    }
    hasChangedValue(oldValue, newValue, ctrlType): boolean {
        this.log.verbose({
            type: 'CompanySettingPresenterComponent:hasChangedValue',
            newValue,
            oldValue,
            equal: oldValue === newValue
        });
        if (oldValue === newValue) {
            // this.editing = null;
            return false;
        }
        if (ctrlType === 'datetimepicker') {
            this.log.verbose({
                type: 'CompanySettingPresenterComponent:hasChangedValue:datetimepicker',
                newValue,
                oldValue,
                days: moment(oldValue).diff(moment(newValue), 'days', true)
            });
            if (Math.abs(moment(oldValue).diff(moment(newValue), 'days')) < 1) {
                return false;
            }
        }
        return true;
    }
}
